import React, { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import useResizeObserver from 'use-resize-observer';

import { RcSlider } from 'common/components/RcSlider';

import styles from './styles.module.scss';
import useSVGScale from './useSvgScale';

type TPropsRcSlider = React.ComponentProps<typeof RcSlider>;

interface IPropsContentMainScalable {
  readonly content: string;
  readonly onChangeSizesInitial: (x: {
    readonly height: number;
    readonly width: number;
    readonly refElement: React.RefObject<HTMLDivElement>;
  }) => void;
  readonly propsRoot?: React.ButtonHTMLAttributes<HTMLDivElement>;
}

const maxSizeToScale = 199.9;
const maxPercentageSize = 200;

const ContentMainScalable: React.FunctionComponent<
  IPropsContentMainScalable
> = ({
  content,
  onChangeSizesInitial: onChangeSizes,
  propsRoot,
  ...otherProps
}) => {
  const refHasSetSizesOnce = useRef(false);
  const refRoot = useRef<HTMLDivElement>(null);
  const { width = -1, height = -1 } = useResizeObserver<HTMLDivElement>({
    ref: refRoot,
  });

  useEffect(() => {
    if (!refHasSetSizesOnce.current && height > -1 && width > -1) {
      refHasSetSizesOnce.current = true;

      onChangeSizes({
        height,
        width,
        refElement: refRoot,
      });
    }
  }, [onChangeSizes, height, width]);

  return (
    <div
      className={cn(
        styles.rootContentMain,
        'content-item-container',
        'css-override',
        propsRoot?.className
      )}
      // eslint-disable-next-line jam3/no-sanitizer-with-danger
      dangerouslySetInnerHTML={{
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        __html: content,
      }}
      ref={refRoot}
      {...propsRoot}
      {...otherProps}
    />
  );
};

interface ContentScalableProps {
  readonly content: string;
  readonly onInitializeSliderProps?: (x: TPropsRcSlider) => void;
  readonly valueSVGScalePercent: number;
}
interface SizesContentMain {
  readonly height: number;
  readonly width: number;
}

export const ContentScalable: React.FC<ContentScalableProps> = ({
  content,
  onInitializeSliderProps,
  valueSVGScalePercent,
}) => {
  const scaleValue = useSVGScale(valueSVGScalePercent, content);

  const [sizesContentMain, setSizesContentMain] =
    useState<SizesContentMain | null>(null);

  const getters = {
    styles: {
      root: (): undefined | React.CSSProperties => {
        if (sizesContentMain) {
          const scaleFactor = scaleValue / 100;

          return {
            height: `${scaleFactor * sizesContentMain.height}px`,
          };
        }

        return undefined;
      },

      contentMain: (): undefined | React.CSSProperties => {
        if (sizesContentMain) {
          return {
            transform: `scale(${scaleValue / 100})`,
          };
        }

        return undefined;
      },
    },
  };

  const onChangeSizes = (({ height, width, refElement }) => {
    setSizesContentMain({
      height,
      width,
    });
    const widthParent = parseInt(
      window
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        .getComputedStyle(refElement.current!.parentElement!)
        .width?.replace('px', ''),
      10
    );

    // because of rounding, we only use 99.xxxx so it does not exceed the parent width
    const scaleToFitParentBy100 = (widthParent / width) * maxSizeToScale;
    const scaleMinimumBy100 = scaleToFitParentBy100 / maxPercentageSize;

    // eslint-disable-next-line react/destructuring-assignment
    onInitializeSliderProps?.({
      // @TODO restore data
      defaultValue: Math.min(
        maxSizeToScale,
        Math.max(0, scaleToFitParentBy100)
      ),
      // 25 percent
      min: scaleMinimumBy100 * 25,
      max: scaleToFitParentBy100,
      step: scaleMinimumBy100 * 5,
    });
  }) as IPropsContentMainScalable['onChangeSizesInitial'];

  /**
   * Scaling causes the width to escalate to much, the inner one is to control it's escalating width
   */

  return (
    <div
      className={styles.root}
      style={getters.styles.root()}
      data-testid="root"
    >
      <div className={styles.rootContentMain}>
        <ContentMainScalable
          content={content}
          onChangeSizesInitial={onChangeSizes}
          propsRoot={{
            style: getters.styles.contentMain(),
          }}
          data-testid="content-main"
        />
      </div>
    </div>
  );
};
